import React from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import { useShiftChangeRequests } from '@/hooks/attendance/management/useShiftChangeRequests';
import { SHIFT_REQUEST_STATUS, type ShiftRequestStatus } from '@/api/domains/shift/shift.types';
import { PermissionGuard } from '@/components/layout';


export const ShiftChangeRequests: React.FC = () => {
  const { t: translate } = useTranslation();
  
  const {
    filters,
    setFilters,
    data,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    actions,
    openNewRequestPanel,
  } = useShiftChangeRequests();

  // Status filter options
  const statusOptions = [
    { value: 'all', label: translate('attendance:shiftChange.status.all') },
    {
      value: SHIFT_REQUEST_STATUS.PENDING.toString(),
      label: translate('attendance:shiftChange.status.pending'),
    },
    {
      value: SHIFT_REQUEST_STATUS.APPROVED.toString(),
      label: translate('attendance:shiftChange.status.approved'),
    },
    {
      value: SHIFT_REQUEST_STATUS.REJECTED.toString(),
      label: translate('attendance:shiftChange.status.rejected'),
    },
    {
      value: SHIFT_REQUEST_STATUS.CANCELED.toString(),
      label: translate('attendance:shiftChange.status.canceled'),
    },
  ];

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="shift-change-requests-page">
        {/* Header with New Request Button */}
        <div>
          <PermissionGuard action="create">
            <PrimaryButton
              onClick={openNewRequestPanel}
              iconLeft={<IconScout name="plus" />}
            >
              {translate('attendance:shiftChange.newRequest')}
            </PrimaryButton>
          </PermissionGuard>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <FilterDropdown
            values={{
              status: filters.status?.toString() || undefined,
              dateFrom: filters.dateFrom,
              dateTo: filters.dateTo,
            }}
            extraFilters={(
              <div className="flex flex-col gap-3 pt-2">
                <Selector
                  label={translate('attendance:shiftChange.filters.status')}
                  options={statusOptions}
                  value={filters.status === 'all' ? 'all' : filters.status?.toString() || 'all'}
                  onChange={(value) => {
                    const statusValue = value === 'all' ? 'all' : (Number(value) as ShiftRequestStatus);
                    setFilters((previous) => ({
                      ...previous,
                      status: statusValue,
                    }));
                  }}
                />
              </div>
            )}
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'requestDate', direction: 'desc' }}
          data-testid="table-shift-change-requests"
          actions={actions}
        />
      </div>
    </PageLayout>
  );
};

export default ShiftChangeRequests;
